import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Loader2, Check, Globe, ChevronRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Mandatory, non-dismissible location gate. Every signed-in member (in Bharat or
 * worldwide) must set a location before using the app. Flow:
 *  0) On open, auto-detect an approximate location from the member's IP (no permission
 *     prompt). If found, it is saved silently and the gate closes.
 *  1) IP couldn't be resolved → "Use my precise location" → browser GPS → reverse-geocode
 *  2) Confirm the detected place → saved via backend → gate closes
 *  3) GPS denied / not correct → pick your country from a list (fallback, still saved)
 */
export default function LocationPrompt() {
  const { user, meta, refreshUser, setCountry } = useApp();
  const [phase, setPhase] = useState("detecting"); // detecting | ask | detected | manual
  const [busy, setBusy] = useState(false);
  const [detected, setDetected] = useState(null); // {lat,lng,accuracy,place,country_code}
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  const ipTried = useRef(false);

  // Only after the member has accepted the consent gate, and only until a location is stored.
  const needsLocation = !!user && !!user.consent?.terms && !user.geo;

  const countries = meta?.countries || [];
  const filtered = useMemo(
    () => countries.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 60),
    [countries, q]
  );

  // Auto-detect from IP as soon as the gate is needed — no GPS permission required.
  useEffect(() => {
    if (!needsLocation || ipTried.current) return;
    ipTried.current = true;
    (async () => {
      try {
        const { data } = await api.get("/me/ip-location");
        if (data?.ok && data.country_code) {
          await api.post("/me/location", {
            lat: data.lat, lng: data.lng, accuracy: 50000,
            place: data.place, country_code: data.country_code, source: "ip",
          });
          if (data.country_code) setCountry(data.country_code);
          toast.success(data.place ? `Location set from your network: ${data.place} 🌍` : "Location set 🌍");
          await refreshUser?.();
          return; // gate closes on next render (user.geo now set)
        }
      } catch { /* fall through to manual/GPS */ }
      setPhase("ask");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsLocation]);

  if (!needsLocation) return null;

  const useGps = () => {
    if (!("geolocation" in navigator)) { toast.error("Location isn't available on this device."); setPhase("manual"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords;
          // Resolve a readable place in the browser (the provider bans server-side calls).
          let place = "", country_code = "";
          try {
            const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
            const d = await r.json();
            place = [d.city, d.principalSubdivision, d.countryName].filter(Boolean).join(", ");
            country_code = (d.countryCode || "").toUpperCase();
          } catch { /* keep coords-only */ }
          setDetected({ lat: latitude, lng: longitude, accuracy, place, country_code });
          setPhase("detected");
        } catch { toast.error("Couldn't detect your location — please pick your country."); setPhase("manual"); }
        finally { setBusy(false); }
      },
      () => { setBusy(false); toast("Location permission was blocked — pick your country instead."); setPhase("manual"); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  const confirmGps = async () => {
    if (!detected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/location", {
        lat: detected.lat, lng: detected.lng, accuracy: detected.accuracy,
        place: detected.place, country_code: detected.country_code,
      });
      if (data.country_code) setCountry(data.country_code);
      toast.success(data.place ? `Location confirmed: ${data.place} 🌍` : "Location saved 🌍");
      await refreshUser?.();
    } catch { toast.error("Couldn't save — try again."); setBusy(false); }
  };

  const saveManual = async () => {
    if (!picked) { toast.error("Please select your country."); return; }
    const c = countries.find((x) => x.code === picked);
    setBusy(true);
    try {
      const { data } = await api.post("/me/location", { manual_country: picked, manual_place: c?.name || picked });
      setCountry(picked);
      toast.success(`Location set: ${c?.name || picked} 🌍`);
      await refreshUser?.();
    } catch { toast.error("Couldn't save — try again."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="location-gate">
      <div className="rud-card rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center gap-2 text-brand mb-1"><MapPin size={18} /><span className="text-[11px] uppercase tracking-widest">One quick step</span></div>

        {phase === "detecting" && (
          <>
            <h2 className="font-serif text-2xl">Finding your location…</h2>
            <p className="text-sec text-sm mt-2 leading-relaxed">
              We're setting your region automatically from your network so you can jump straight in.
            </p>
            <div className="w-full h-11 rounded-full mt-4 flex items-center justify-center gap-2 text-sm text-sec" data-testid="location-detecting">
              <Loader2 size={16} className="animate-spin text-brand" /> One moment…
            </div>
          </>
        )}

        {phase === "ask" && (
          <>
            <h2 className="font-serif text-2xl">Set your location</h2>
            <p className="text-sec text-sm mt-2 leading-relaxed">
              My Kind uses your location for local community, weather, events, nearby members and region-fair pricing.
              With your permission we collect it — it stays private and is never shared publicly.
            </p>
            <button onClick={useGps} disabled={busy} data-testid="location-use-gps"
              className="btn-primary w-full h-11 rounded-full mt-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />} Use my precise location
            </button>
            <button onClick={() => setPhase("manual")} disabled={busy} data-testid="location-pick-manual"
              className="btn-ghost w-full h-10 rounded-full mt-2 text-sm flex items-center justify-center gap-2">
              <Globe size={15} /> Or pick my country instead
            </button>
          </>
        )}

        {phase === "detected" && (
          <>
            <h2 className="font-serif text-2xl">Is this right?</h2>
            <p className="text-sec text-sm mt-2">We detected your location as:</p>
            <div className="rud-card rounded-xl p-4 mt-2 flex items-center gap-3" data-testid="location-detected">
              <MapPin size={18} className="text-brand shrink-0" />
              <span className="text-[15px] font-medium">{detected?.place || detected?.country_code || "Unknown place"}</span>
            </div>
            <button onClick={confirmGps} disabled={busy} data-testid="location-confirm"
              className="btn-primary w-full h-11 rounded-full mt-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Yes, confirm this location
            </button>
            <button onClick={() => setPhase("manual")} disabled={busy} data-testid="location-reject"
              className="btn-ghost w-full h-10 rounded-full mt-2 text-sm">No, let me pick my country</button>
          </>
        )}

        {phase === "manual" && (
          <>
            <h2 className="font-serif text-2xl">Pick your country</h2>
            <p className="text-sec text-sm mt-2">Choose your country/region to continue.</p>
            <input
              data-testid="location-country-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search country…" className="rud-input w-full rounded-lg px-3 py-2 text-sm mt-3"
            />
            <div className="max-h-56 overflow-y-auto mt-2 rounded-lg border border-rud divide-y divide-white/5">
              {filtered.map((c) => (
                <button key={c.code} data-testid={`location-country-${c.code}`} onClick={() => setPicked(c.code)}
                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors ${picked === c.code ? "bg-brand/15 text-brand" : "hover:bg-white/5"}`}>
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {picked === c.code ? <Check size={14} /> : <ChevronRight size={14} className="text-sec" />}
                </button>
              ))}
              {filtered.length === 0 && <div className="px-3 py-3 text-sec text-sm">No matches.</div>}
            </div>
            <button onClick={saveManual} disabled={busy || !picked} data-testid="location-manual-save"
              className="btn-primary w-full h-11 rounded-full mt-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save my location
            </button>
            <button onClick={() => setPhase("ask")} disabled={busy} data-testid="location-back"
              className="btn-ghost w-full h-10 rounded-full mt-2 text-sm">← Back to precise location</button>
          </>
        )}

        <p className="text-[10px] text-sec text-center mt-3">You gave permission to collect your location. You can change it anytime from your profile.</p>
      </div>
    </div>
  );
}
